import { Fragment, StrictMode, useLayoutEffect } from 'react';
import { Metrics } from './metrics';
import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';
// @ts-expect-error untyped loader
import { name as packageName } from 'current-package-loader!';
import { getShouldStartWithCopilotOpen } from './copilot';
import { GridStyles, setupGridClasses } from './grid';
import { QUARTZ_APP_CONTAINER_CLASS } from 'quartz/grid';
import { clearMarks, clearMeasures, getEntriesByName, mark, measure } from './perf';
import { QUARTZ_TRELLIS_MAIN_CONTENT_CLASS } from 'quartz/trellis-page-layout-styles';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function Root({
  children
}) {
  useLayoutEffect(() => {
    mark('quartz.afterRootRender');

    // `performance.mark()` doesn't return a performance `PerformanceMark` in some browsers, so we use `getEntriesByName`
    // See: https://developer.mozilla.org/en-US/docs/Web/API/Performance/mark#browser_compatibility
    getEntriesByName('quartz.appEntry', 'mark').forEach(entry => {
      Metrics.timer('appEntry').update(entry.startTime);
    });
    getEntriesByName('quartz.authFinished', 'mark').forEach(entry => {
      Metrics.timer('authFinished').update(entry.startTime);
    });
    clearMarks('quartz.authFinished');
    getEntriesByName('quartz.prepareAppFinished', 'mark').forEach(entry => {
      Metrics.timer('prepareAppFinished').update(entry.startTime);
    });
    clearMarks('quartz.prepareAppFinished');
    getEntriesByName('quartz.beforeRootRender', 'mark').forEach(entry => {
      Metrics.timer('beforeRootRender').update(entry.startTime);
    });
    measure('quartz.rootRender', 'quartz.beforeRootRender', 'quartz.afterRootRender', {
      metricTrackingName: 'rootRender'
    });
    clearMarks('quartz.afterRootRender');

    // The rough time it takes to get from our app entry to the handoff to the
    // app render
    measure('quartz.startupTime', 'quartz.appEntry', 'quartz.beforeRootRender', {
      metricTrackingName: 'appStartup'
    });
    clearMarks('quartz.appEntry');
    clearMarks('quartz.beforeRootRender');
    clearMeasures('quartz.rootRender');
    clearMeasures('quartz.startupTime');
  }, []);
  return children;
}
function getLegacyRootLocalStorageOverride() {
  try {
    const value = localStorage.getItem('LEGACY_ROOT_OVERRIDE');
    const parsedValue = value !== null ? JSON.parse(value) : null;
    return typeof parsedValue === 'boolean' ? parsedValue : null;
  } catch (_unused) {
    return null;
  }
}
export function renderRoot(reactElement, {
  strictMode = false,
  legacyRoot = true,
  copilotLayoutType,
  isCopilotDisabled = false,
  container: containerFromOptions,
  hasTrellisModernizedLayout = false
} = {}) {
  let container = containerFromOptions !== null && containerFromOptions !== void 0 ? containerFromOptions : null;
  if (!container) {
    if (hasTrellisModernizedLayout) {
      container = document.querySelector(`.${QUARTZ_TRELLIS_MAIN_CONTENT_CLASS}`);
      if (!container) {
        Metrics.counter('quartz.trellisLayout.noContainer').increment();
      }
    } else if (copilotLayoutType && copilotLayoutType !== 'panel') {
      container = document.querySelector(`.${QUARTZ_APP_CONTAINER_CLASS}`);
      if (!container) {
        Metrics.counter('quartz.copilotLayout.noContainer').increment();
        container = document.querySelector('.page');
      }
    } else {
      container = document.querySelector('.page');
    }
  }
  if (!container) {
    throw new Error('Invariant: Could not find container element');
  }
  if (copilotLayoutType) {
    setupGridClasses(copilotLayoutType);
  }
  const StrictModeIfEnabled = strictMode ? StrictMode : Fragment;
  const children = /*#__PURE__*/_jsx(Root, {
    children: /*#__PURE__*/_jsxs(StrictModeIfEnabled, {
      children: [copilotLayoutType && /*#__PURE__*/_jsx(GridStyles, {
        shouldSidebarStartOpen: !isCopilotDisabled && getShouldStartWithCopilotOpen()
      }), reactElement]
    })
  });
  mark('quartz.beforeRootRender');
  const legacyRootOverride = getLegacyRootLocalStorageOverride();
  if (legacyRootOverride !== null) {
    legacyRoot = legacyRootOverride;
  }

  // ReactDOM.render is removed in React 19, so the legacy path only works on
  // React 18; fall back to createRoot when it's unavailable.
  // eslint-disable-next-line react/no-deprecated
  const supportsLegacyRoot = typeof ReactDOM.render === 'function';
  if (!legacyRoot || !supportsLegacyRoot) {
    const root = createRoot(container, {
      identifierPrefix: packageName
    });
    root.render(children);
    return root;
  } else {
    // Default import, not `import { render }`, so this builds on both React 18
    // and 19 (React 19 drops the named export; a named import would be a hard
    // bundler error).
    // eslint-disable-next-line react/no-deprecated
    ReactDOM.render(children, container);
    return {
      unmount() {
        // eslint-disable-next-line react/no-deprecated
        ReactDOM.unmountComponentAtNode(container);
      }
    };
  }
}